import { Callout, Checkbox, CommandBarButton, DirectionalHint, IColumn, IconButton, SearchBox, TooltipHost } from '@fluentui/react';
import { useBoolean, useId } from '@fluentui/react-hooks';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { PodCreateRequest, PodSearchResult } from '../../api/pods.types';
import { ROUTES } from '../../config/routes';
import { toast } from '../../redux/message/actions';
import { ToastType } from '../../redux/message/types';
import { useDebounce, useQueryString } from '../../util/hooks';
import useLocalStorage from '../../util/hooks/useLocalStorage';
import { PageHeader } from '../core/PageHeader';
import useCreatePodDialog from './hooks/useCreatePodDialog';
import styles from './PodSearchPage.module.scss';
import PodMarketChips from './PodMarketChips';
import PodPlaceholder from './PodPlaceholder';
import PodShimmeredTable from './PodShimmeredTable';
import PodStatusBadge from './PodStatusBadge';
import { setSearchError, setSearchResults, setIsSearching } from './state/actions';
import { PodStateContext } from './state/reducer';
import { getSearchState, getUserState } from './state/selectors';
import { Link } from 'react-router-dom';
import { createPod, getPodsByProviderId, getPodsByQuery } from '../../api/pods';
import PodErrorBoundary from './PodErrorBoundary';

const buildColumns = (): IColumn[] => [
  {
    key: 'name',
    name: 'Name',
    minWidth: 150,
    maxWidth: 220,
    targetWidthProportion: 2,
    flexGrow: 2,
    className: styles.badgeCell,
    onRender: (item: PodSearchResult) => {
      return (
        <>
          {item.name}{item.status === 'DEACTIVATED' && <PodStatusBadge type="DEACTIVATED" size="compact" />}
        </>
      );
    }
  },
  {
    key: 'description',
    name: 'Description',
    fieldName: 'description',
    minWidth: 240,
    flexGrow: 4,
    isMultiline: true,
  },
  {
    key: 'region',
    name: 'Region',
    fieldName: 'region',
    minWidth: 170,
    flexGrow: 1,
  },
  {
    key: 'markets',
    name: 'Market groups',
    minWidth: 220,
    flexGrow: 1,
    onRender: (item: PodSearchResult) => {
      return <PodMarketChips chips={item.markets} />;
    }
  },
  {
    key: 'physician',
    name: 'Physician',
    minWidth: 220,
    flexGrow: 2,
    onRender: (item: PodSearchResult) => {
      const leadProvider = item.leadProvider;

      if (!leadProvider) {
        return null;
      } else {
        return `${leadProvider.firstName} ${leadProvider.lastName}${leadProvider.suffix ? `, ${leadProvider.suffix}` : ''}`;
      }
    }
  },
  {
    key: 'actions',
    name: 'Actions',
    minWidth: 0,
    flexGrow: 1,
    onRender: (item: PodSearchResult) => {
      return (
        <div className={styles.rowActions} aria-label="Actions">
          <TooltipHost
            content='View pod'
            directionalHint={DirectionalHint.topRightEdge}
          >
            {/* Disable tab navigation on the link, since the IconButton is focusable by default. */}
            <Link to={`${ROUTES.pods}/${item.id}`} state={{ canStepBackToSearch: true, }} tabIndex={-1} data-is-focusable={false}>
              <IconButton
                aria-label="View pod" 
                className={styles.rowAction}
                iconProps={{
                  iconName: 'NavigateExternalInline', // Would like to use PopExpand, but it doesn't exist in our version of the icon set.
                }}
              />
            </Link>
          </TooltipHost>
        </div>
      )
    }
  },
];

const PodsSearchPage = () => {
  const filterButtonId = useId('filter-button');
  const [filterCalloutVisible, {toggle: setFilterCalloutVisible}] = useBoolean(false);
  const [filterShowInactive, setFilterShowInactive] = useLocalStorage('pods-showDeactivatedPods', false);

  const columns = useRef<IColumn[]>(buildColumns());
  const [searchText, setSearchText] = useQueryString('q', '');
  const debouncedSearchText = useDebounce(searchText, 500);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sendToast = (type: ToastType, title: string, content?: string) => dispatch(toast({ type, title, content }));

  const onSubmitSearch = () => {
    contextDispatch(setIsSearching(debouncedSearchText));
    getPodsByQuery(debouncedSearchText)
      .then((pods) => {
        contextDispatch(setSearchResults(pods));
      })
      .catch((error) => {
        contextDispatch(setSearchError(error));
      });
  }

  const onSubmitCreatePod = async (data: PodCreateRequest, navigateOnSuccess: boolean) => {
    setCreatePodLoading(true);

    const physicianIsUnassigned = await getPodsByProviderId(data.leadProviderId)
      .then(pods => {
        return pods.length === 0;
      })
      .catch(error => {
        // If we're unable to check for the provider's membership, fail out.
        return undefined;
      });
    
    if (physicianIsUnassigned === false) {
      setCreatePodError('This physician is already assigned to a pod.');
      return;
    } else if (physicianIsUnassigned === undefined) {
      setCreatePodError('Unable to create the pod. Please try again.');
      return;
    }

    createPod(data)
      .then(pod => {
        const createdPodId = pod.id;
        const createdPodName = pod.name;
        sendToast('success', 'Pod created', `The pod ${createdPodName} has been created.`);

        if (navigateOnSuccess) {
          navigate(`${ROUTES.pods}/${createdPodId}`);
        } else {
          hideCreatePodDialog();
          onSubmitSearch();
        }
      })
      .catch(error => {
        setCreatePodError(error.message ?? 'An error occurred. Please try again.');
        sendToast('error', 'Unable to create', 'Unable to create the pod. Please try again.');
        setCreatePodLoading(false);
      });
  };

  const [
    createPodDialog,
    showCreatePodDialog,
    hideCreatePodDialog,
    setCreatePodLoading,
    setCreatePodError
  ] = useCreatePodDialog(onSubmitCreatePod);

  const {useContextSelector, contextDispatch} = useContext(PodStateContext);
  const searchState = useContextSelector(getSearchState);
  const userIsOpsManager = useContextSelector(getUserState).isManager;

  const filteredRows = useMemo(() => {
    if (filterShowInactive) {
      return searchState.results;
    } else {
      return searchState.results.filter(pod => pod.status === 'ACTIVATED');
    }
  }, [searchState.results, filterShowInactive]);

  useEffect(() => {
    // Execute the search API call.
    onSubmitSearch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText, contextDispatch]);

  return (
    <main className={styles.podsContainer}>
      <PageHeader
        title='Pods'
        documentTitle={searchState.error ? 'Error' : 'Pods'}
        actions={{
          items: [
            {
              key: 'newPod',
              text: 'Create pod',
              disabled: !userIsOpsManager,
              iconProps: { iconName: 'Add' },
              onRender: (props) => (
                <CommandBarButton {...props} onRenderText={(props, defaultRender) => (
                  <TooltipHost
                    content={!userIsOpsManager ? 'You don’t have permission to create pods.' : ''}
                    styles={{
                      root: {
                        display: 'flex',
                      }
                    }}
                    directionalHint={DirectionalHint.bottomRightEdge}
                  >
                    
                    {defaultRender!(props)}
                  </TooltipHost>
                )} />
              ),
              onClick: () => {
                showCreatePodDialog();
              }
            }
          ],
        }}
      />

      {createPodDialog}

      <div className={styles.searchWrapper}>
        <SearchBox
          placeholder='Search for pods...'
          onChange={(_, v) => setSearchText(v ?? '')}
          defaultValue={searchText}
        />
          <div>
            <IconButton
              id={filterButtonId}
              iconProps={{ iconName: 'Filter' }}
              onClick={setFilterCalloutVisible}
              ariaLabel='Show filters'
            />
            {filterCalloutVisible && (
              <Callout
                role='dialog'
                directionalHint={DirectionalHint.bottomRightEdge}
                onDismiss={setFilterCalloutVisible}
                target={`#${filterButtonId}`}
                className={styles.filterCallout}
                setInitialFocus
              >
                <Checkbox
                  label='Show inactive'
                  checked={filterShowInactive}
                  onChange={(_, checked) => setFilterShowInactive(!!checked)}
                  boxSide='end'
                  onRenderLabel={(props) => <span className={styles.filterCheckboxLabel}>{props?.label}</span>}
                  inputProps={{
                    tabIndex: 0,
                  }}
                />
              </Callout>
            )}
          </div>
      </div>
      <PodErrorBoundary>
        <PodShimmeredTable
          columns={columns.current}
          items={filteredRows}
          enableShimmer={searchState.loading || searchText !== searchState.query}
          onRenderEmptyState={() => {
            if (searchState.error) {
              const loadAction = searchState.query !== '' ? 'search for' : 'load the list of';
              return <PodPlaceholder type="error" iconName="Cancel" text={`Unable to ${loadAction} pods.`} output={searchState.error.diagnostic ?? searchState.error.message} />;
            }
            
            if (searchState.query && searchState.results.length === 0) {
              return <PodPlaceholder iconName="Search" text="Couldn’t find any matching pods." />;
            } else if (searchState.results.length > 0) {
              return <PodPlaceholder iconName="Filter" text="All results are hidden by filters." action={{
                iconName: "ClearFilter",
                text: "Disable filters",
                onClick: () => {
                  setFilterShowInactive(true)
                }
              }} />
            } else {
              return <PodPlaceholder iconName="WebAppBuilderModule" text="No pods have been created." action={{
                iconName: "Add",
                text: "Create pod",
                disabled: !userIsOpsManager,
                disabledTooltip: 'You don’t have permission to create pods.',
                onClick: () => showCreatePodDialog()
              }} />
            }
          }}
        />
      </PodErrorBoundary>
    </main>
  )
}

export default PodsSearchPage;